import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  Gift, Shield, Zap, Sparkles, TrendingUp, ArrowRight, Star, X,
  CheckCircle2, Globe2,
} from 'lucide-react';
import { trackEvent } from '../../utils/analytics';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const STORAGE_KEY = 'varotrades_promo_seen_at_v1';
const VARIANT_KEY = 'varotrades_promo_variant_v1';
const COOLDOWN_DAYS = 7;
const SHOW_AFTER_MS = 10000;

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---------- A/B test variants (50/50 split, sticky per visitor) ----------
const getVariant = () => {
  try {
    let v = localStorage.getItem(VARIANT_KEY);
    if (v !== 'A' && v !== 'B') {
      v = Math.random() < 0.5 ? 'A' : 'B';
      localStorage.setItem(VARIANT_KEY, v);
    }
    return v;
  } catch { return 'A'; }
};

// Routes where the popup must NOT show
const BLOCKED_PATH_PREFIXES = [
  '/admin', '/dashboard', '/settings', '/kyc', '/funds',
  '/wallet', '/register', '/login', '/vwallet', '/copy-trading',
  '/super-admin', '/crm',
];

const seenRecently = () => {
  // User explicitly requested: popups should re-show on every page reload, even if previously dismissed.
  // The session-level guard inside the component itself prevents flicker within a single page lifetime.
  return false;
};

const markSeen = () => {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
};

// ---------- Animated counter ----------
const Counter = ({ to, prefix = '', suffix = '', duration = 1.4, decimals = 0 }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return (
    <span className="tabular-nums font-mono">
      {prefix}{v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}
    </span>
  );
};

// ---------- Animated chart bars (cinematic) ----------
const ChartBars = ({ isRTL }) => {
  const bars = useMemo(() => [
    { h: 38, d: 0.05 }, { h: 56, d: 0.10 }, { h: 44, d: 0.15 },
    { h: 72, d: 0.20 }, { h: 60, d: 0.25 }, { h: 88, d: 0.30 },
    { h: 76, d: 0.35 }, { h: 100, d: 0.40 }, { h: 92, d: 0.45 },
  ], []);
  return (
    <div className={`absolute inset-x-0 bottom-0 px-6 pb-6 flex items-end gap-1.5 h-32 ${isRTL ? 'flex-row-reverse' : ''}`} aria-hidden>
      {bars.map((b, i) => (
        <motion.div
          key={i}
          initial={{ height: 0, opacity: 0.5 }}
          animate={{ height: `${b.h}%`, opacity: 1 }}
          transition={{ delay: b.d, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex-1 rounded-t-md"
          style={{
            background: `linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.55) 60%, rgba(255,255,255,0.18) 100%)`,
            boxShadow: '0 -2px 12px rgba(255,255,255,0.25)',
          }}
        />
      ))}
    </div>
  );
};

// ---------- Floating sparkles ----------
const Sparkle = ({ x, y, delay, size = 8 }) => (
  <motion.div
    className="absolute pointer-events-none"
    style={{ left: `${x}%`, top: `${y}%` }}
    initial={{ opacity: 0, scale: 0, rotate: 0 }}
    animate={{ opacity: [0, 1, 0], scale: [0, 1, 0], rotate: 180 }}
    transition={{ delay, duration: 1.8, repeat: Infinity, repeatDelay: 2.2 }}
  >
    <svg width={size} height={size} viewBox="0 0 24 24" fill="white">
      <path d="M12 0l2.2 9.8L24 12l-9.8 2.2L12 24l-2.2-9.8L0 12l9.8-2.2z" />
    </svg>
  </motion.div>
);

// ---------- Animated ticker pills ----------
const InstrumentTicker = ({ isRTL }) => {
  const items = [
    { sym: 'XAU/USD', val: '+2.14%' },
    { sym: 'BTC/USD', val: '+1.78%' },
    { sym: 'EUR/USD', val: '+0.42%' },
    { sym: 'US500',   val: '+0.91%' },
    { sym: 'GBP/USD', val: '+0.36%' },
    { sym: 'XAG/USD', val: '+1.22%' },
  ];
  return (
    <div className={`flex gap-2 overflow-hidden ${isRTL ? 'flex-row-reverse' : ''}`} aria-hidden>
      {items.map((it, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 + i * 0.06, duration: 0.4 }}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/12 border border-white/15 backdrop-blur-sm"
        >
          <span className="text-[10px] font-mono text-white/85">{it.sym}</span>
          <span className="text-[10px] font-bold text-emerald-300">{it.val}</span>
        </motion.div>
      ))}
    </div>
  );
};

// ===============================================================
// MAIN POPUP
// ===============================================================
const PromoPopup = () => {
  const [open, setOpen] = useState(false);
  useBodyScrollLock(open);
  const [variant, setVariant] = useState('A');
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const t = (ar, en) => (isRTL ? ar : en);

  // Variant content
  const v = useMemo(() => {
    if (variant === 'B') {
      return {
        badge: t('جرّب مجاناً · بدون إيداع', 'Try free · No deposit needed'),
        heroAmount: '$10K',
        heroSub: t('حساب تجريبي بـ 10,000$ افتراضية لتختبر السوق', '$10,000 demo account · risk-free practice'),
        headlinePre: t('تدرّب على ', 'Practise on '),
        headlineAccent: t('الأسواق الحقيقية', 'real markets'),
        headlinePost: t(' بدون مخاطرة', ' risk-free'),
        intro: t(
          'افتح حساباً تجريبياً مجانياً واحصل على $10,000 افتراضية فوراً. نفس الأسعار اللحظية، نفس التنفيذ، صفر مخاطر.',
          'Open a free demo and get $10,000 in virtual funds instantly. Same live prices, same execution, zero risk.'
        ),
        ctaText: t('افتح حساباً تجريبياً مجاناً', 'Start my free demo'),
        utmCampaign: 'free_demo_10k',
      };
    }
    return {
      badge: t('عرض حصري · لفترة محدودة', 'Exclusive · Limited time'),
      heroAmount: '50%',
      heroSub: t('على أول إيداع · حتى $5,000 من بونص التداول', 'On your first deposit · up to $5,000 in trading bonus'),
      headlinePre: t('ابدأ تداولك بـ ', 'Start trading with '),
      headlineAccent: t('قوة إضافية', 'extra firepower'),
      headlinePost: '',
      intro: t(
        'انضمّ لآلاف المتداولين على VAROTRADES. تنفيذ فوري، فروقات تنافسية، وأموال محمية بتأمين Lloyd\'s of London.',
        'Join thousands of VAROTRADES traders. Instant execution, razor-tight spreads, and funds protected by Lloyd\'s of London insurance.'
      ),
      ctaText: t('سجّل الآن واستلم البونص', 'Claim my 50% bonus'),
      utmCampaign: 'welcome50',
    };
  }, [variant, isRTL]);

  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    if (seenRecently()) return;
    const blocked = BLOCKED_PATH_PREFIXES.some(p => location.pathname.startsWith(p));
    if (blocked) return;
    const assignedVariant = getVariant();
    setVariant(assignedVariant);
    const timer = setTimeout(() => {
      setOpen(true);
      markSeen();
      try { trackEvent('PromoPopup_Shown', { variant: assignedVariant, content_name: assignedVariant === 'B' ? 'free_demo_10k' : 'welcome_bonus_50' }); } catch {}
    }, SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [authLoading, isAuthenticated, location.pathname]);

  const close = (source = 'dismiss') => {
    try { trackEvent('PromoPopup_Dismissed', { variant, source }); } catch {}
    setOpen(false);
  };

  const cta = () => {
    try { trackEvent('PromoPopup_CTA_Click', { variant, content_name: 'register_from_promo' }); } catch {}
    // Stash bonus_code for the registration flow to pick up
    try {
      if (variant === 'A') {
        localStorage.setItem('varotrades_bonus_code', 'WELCOME50');
      } else {
        localStorage.setItem('varotrades_bonus_code', 'DEMO10K');
      }
      localStorage.setItem('varotrades_acquisition', JSON.stringify({
        utm_source: 'site',
        utm_medium: 'promo_popup',
        utm_campaign: v.utmCampaign,
        utm_content: `variant_${variant}`,
      }));
    } catch {}
    setOpen(false);
    navigate(`/register?utm_source=site&utm_medium=promo_popup&utm_campaign=${v.utmCampaign}&utm_content=variant_${variant}`);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="promo-popup"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-ink-900/65 backdrop-blur-[10px]"
            onClick={() => close('backdrop')}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />

          {/* Card — sized to 70% of mobile viewport, max ~88% on tablet+ */}
          <motion.div
            className="promo-popup-card relative w-full max-w-[1040px] rounded-[24px] overflow-hidden bg-white shadow-[0_40px_120px_-20px_rgba(20,15,10,0.55)] border border-white/30"
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
          >
            <style>{`
              .promo-popup-card { max-height: 70vh; }
              @media (min-width: 640px)  { .promo-popup-card { max-height: 88vh; } }
              @media (min-width: 1024px) { .promo-popup-card { max-height: 92vh; } }
              .promo-popup-grid {
                max-height: inherit;
                overflow-y: auto;
                -webkit-overflow-scrolling: touch;
              }
            `}</style>
            {/* Close */}
            <button
              onClick={() => close('close_button')}
              className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} z-30 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-ink-700 hover:text-ink-900 transition-colors`}
              aria-label={t('إغلاق', 'Close')}
              data-testid="promo-close-btn"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="promo-popup-grid grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
              {/* ==================== VISUAL SIDE ==================== */}
              <div className="relative min-h-[180px] sm:min-h-[220px] lg:min-h-[540px] overflow-hidden flex-shrink-0"
                style={{
                  background: 'radial-gradient(120% 80% at 20% 10%, #FF8A3D 0%, #FF5722 38%, #C13A12 78%, #6B1F08 100%)',
                }}>
                {/* Grain */}
                <div className="absolute inset-0 opacity-[0.10] mix-blend-overlay pointer-events-none"
                  style={{
                    backgroundImage:
                      'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27200%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/></svg>")',
                  }} />

                {/* Soft orb top */}
                <div className="absolute -top-20 -left-10 w-72 h-72 rounded-full"
                  style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.30), transparent 65%)' }} />

                {/* Sparkles */}
                <Sparkle x={12} y={18} delay={0.3} size={10} />
                <Sparkle x={78} y={12} delay={0.9} size={7} />
                <Sparkle x={88} y={48} delay={1.5} size={12} />
                <Sparkle x={22} y={56} delay={0.5} size={6} />
                <Sparkle x={60} y={70} delay={1.1} size={9} />

                {/* Floating brand emblem */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ delay: 0.25, type: 'spring', damping: 14, stiffness: 200 }}
                  className="relative z-10 px-5 sm:px-8 pt-5 sm:pt-8 lg:pt-10"
                >
                  <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                    <span className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase text-white">
                      {v.badge}
                    </span>
                  </div>
                </motion.div>

                {/* Hero amount */}
                <div className="relative z-10 px-5 sm:px-8 pt-3 sm:pt-6 lg:pt-8">
                  <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.55 }}
                    className="flex items-baseline gap-2 mb-1"
                  >
                    <span className="text-[clamp(44px,9vw,108px)] font-black leading-[0.85] text-white tracking-tighter drop-shadow-[0_4px_24px_rgba(0,0,0,0.30)]">
                      {v.heroAmount}
                    </span>
                    <span className="text-lg sm:text-2xl font-bold text-white/95">
                      {variant === 'B' ? t('تجريبي', 'Demo') : t('بونص', 'Bonus')}
                    </span>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="text-white/90 text-sm sm:text-base font-semibold leading-snug max-w-[320px]"
                  >
                    {v.heroSub}
                  </motion.div>
                </div>

                {/* Live ticker pills */}
                <div className="relative z-10 px-5 sm:px-8 mt-4 sm:mt-6 lg:mt-8 hidden sm:block">
                  <InstrumentTicker isRTL={isRTL} />
                </div>

                {/* Animated chart at bottom */}
                <ChartBars isRTL={isRTL} />

                {/* Decorative gradient sheen */}
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  initial={{ x: '-100%' }}
                  animate={{ x: '120%' }}
                  transition={{ delay: 1.2, duration: 1.6, ease: 'easeInOut' }}
                  style={{
                    background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.16) 50%, transparent 65%)',
                  }}
                />
              </div>

              {/* ==================== CONTENT SIDE ==================== */}
              <div className="relative bg-white p-5 sm:p-8 lg:p-10 flex flex-col justify-between flex-1 min-h-0">
                {/* Top section */}
                <div>
                  {/* Brand strip — real VAROTRADES emblem */}
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center gap-2.5 mb-4 sm:mb-5"
                  >
                    <img
                      src={`${API_URL}/api/media/varotrade_official_emblem.png`}
                      alt="VAROTRADES"
                      className="h-7 sm:h-8 w-auto"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-[10px] text-ink-400 ms-1">FSC · GB21026396</span>
                  </motion.div>

                  {/* Headline */}
                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.5 }}
                    className="text-[clamp(22px,3.4vw,38px)] font-black text-ink-900 tracking-tight leading-[1.05]"
                  >
                    {v.headlinePre}
                    <span className="relative inline-block">
                      <span className="relative z-10 bg-gradient-to-r from-flame-600 to-[#FF8A3D] bg-clip-text text-transparent">
                        {v.headlineAccent}
                      </span>
                      <motion.span
                        className="absolute inset-x-0 -bottom-0.5 h-2 rounded bg-flame-200/70 -z-0"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 0.7, duration: 0.5 }}
                        style={{ originX: isRTL ? 1 : 0 }}
                      />
                    </span>
                    {v.headlinePost}
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: 0.55 }}
                    className="text-ink-600 text-[13px] sm:text-[15px] leading-relaxed mt-2 sm:mt-3 max-w-[440px]"
                  >
                    {v.intro}
                  </motion.p>

                  {/* Stat row */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 sm:mt-6"
                  >
                    {[
                      { v: 1.4, suffix: 'M+', label: t('متداول', 'Traders'), decimals: 1 },
                      { v: 180, suffix: '+',  label: t('دولة', 'Countries'), decimals: 0 },
                      { v: 50,  suffix: 'ms', label: t('تنفيذ', 'Execution'), prefix: '<', decimals: 0 },
                    ].map((s, i) => (
                      <div key={i} className="text-center">
                        <div className="text-[18px] sm:text-[22px] font-black text-ink-900 leading-none">
                          <Counter to={s.v} prefix={s.prefix || ''} suffix={s.suffix} decimals={s.decimals} duration={1.2 + i * 0.15} />
                        </div>
                        <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.12em] text-ink-500 mt-1">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </motion.div>

                  {/* Bullets */}
                  <motion.ul
                    initial="hidden" animate="visible"
                    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.85 } } }}
                    className="mt-4 sm:mt-6 space-y-1.5 sm:space-y-2.5"
                  >
                    {[
                      { Icon: Gift,       text: variant === 'B' ? t('$10,000 رصيد افتراضي للتدريب', '$10,000 virtual funds for practice') : t('بونص ترحيبي 50% على أول إيداع', '50% welcome bonus on first deposit') },
                      { Icon: Zap,        text: t('سحوبات سريعة وتنفيذ < 50 مللي ثانية', 'Fast withdrawals · Sub-50ms execution') },
                      { Icon: Shield,     text: t('مرخّص FSC موريشيوس · أموال محمية بـ Lloyd\'s', 'Licensed by FSC Mauritius · Lloyd\'s protected') },
                      { Icon: Globe2,     text: t('+1000 أداة تداول · فوركس · ذهب · أسهم · كريبتو', '1000+ instruments · FX · gold · stocks · crypto') },
                    ].map((row, i) => (
                      <motion.li key={i}
                        variants={{ hidden: { opacity: 0, x: isRTL ? 10 : -10 }, visible: { opacity: 1, x: 0 } }}
                        className="flex items-center gap-2.5 text-[12px] sm:text-[13.5px] text-ink-800"
                      >
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-flame-50 text-flame-600 flex items-center justify-center shrink-0">
                          <row.Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.4} />
                        </div>
                        <span>{row.text}</span>
                      </motion.li>
                    ))}
                  </motion.ul>
                </div>

                {/* Bottom section: CTA + trust */}
                <div className="mt-5 sm:mt-7">
                  <motion.button
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, type: 'spring', damping: 18 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={cta}
                    className="relative group w-full h-[52px] sm:h-[58px] rounded-2xl text-white font-black text-[14px] sm:text-[16px] tracking-tight overflow-hidden"
                    style={{
                      background: 'linear-gradient(105deg, #FF5722 0%, #FF8A3D 100%)',
                      boxShadow: '0 14px 38px -10px rgba(255,87,34,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
                    }}
                    data-testid="promo-cta-btn"
                  >
                    <span className="relative z-10 inline-flex items-center gap-2">
                      {v.ctaText}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''} transition-transform group-hover:translate-x-0.5`} />
                    </span>
                    {/* Animated sheen */}
                    <motion.span
                      className="absolute inset-0 pointer-events-none"
                      style={{ background: 'linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.45) 50%, transparent 62%)' }}
                      animate={{ x: ['-110%', '110%'] }}
                      transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.4, ease: 'easeInOut' }}
                    />
                  </motion.button>

                  <button
                    onClick={() => close('no_thanks')}
                    className="block w-full text-center text-[11px] sm:text-[12px] text-ink-500 hover:text-ink-700 font-semibold mt-2 sm:mt-3 transition-colors"
                    data-testid="promo-no-thanks"
                  >
                    {t('لا، شكراً · ربما لاحقاً', 'No thanks · maybe later')}
                  </button>

                  {/* Trust strip — hidden on very short mobile to fit 70vh */}
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: 1.3 }}
                    className="hidden sm:flex items-center justify-between gap-3 mt-5 pt-5 border-t border-[rgba(20,15,10,0.06)]"
                  >
                    <div className="flex items-center gap-1.5">
                      <div className="flex">
                        {[0,1,2,3,4].map(i => (
                          <Star key={i} className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" />
                        ))}
                      </div>
                      <span className="text-[11px] font-bold text-ink-700">4.8 · Trustpilot</span>
                    </div>
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-ink-500">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      {t('حماية أموال $1M', '$1M fund protection')}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-ink-500">
                      <TrendingUp className="w-3 h-3 text-flame-600" />
                      {t('منذ 2021', 'Since 2021')}
                    </div>
                  </motion.div>

                  {/* Compact trust row for mobile */}
                  <div className="flex sm:hidden items-center justify-center gap-3 mt-3 pt-3 border-t border-[rgba(20,15,10,0.06)] text-[10px] font-semibold text-ink-500">
                    <span className="flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" />
                      4.8 Trustpilot
                    </span>
                    <span>·</span>
                    <span>FSC Licensed</span>
                    <span>·</span>
                    <span>Since 2021</span>
                  </div>

                  <p className="hidden sm:block text-[10px] text-ink-400 mt-3 leading-relaxed">
                    {t(
                      '* الشروط والأحكام تنطبق. تداول العقود مقابل الفروقات ينطوي على مخاطر عالية وقد لا يناسب جميع المستثمرين.',
                      '* T&Cs apply. CFD trading carries high risk and may not be suitable for all investors.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PromoPopup;
